import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Button, Modal } from "react-bootstrap";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  fetchUserProfile,
  fetchUserSubscriptions,
  createReferral,
} from "../apiService";
import ReferralLoginModal from "../Components/ReferralLoginModal";
import { useAuth } from "../hooks/useAuth";
// import "./../App.css";

// South African mobile format: 10 digits, starting with 0
const SA_MOBILE_PATTERN = /^0\d{9}$/;

// Header titles for the single login modal. ReferralLoginModal reports
// which screen it's on (onModeChange) and the header here follows it.
type LoginMode = "login" | "forgot" | "confirm";

const LOGIN_TITLES: Record<LoginMode, string> = {
  login: "Login",
  forgot: "Forgot Password",
  confirm: "Confirm Password Reset",
};

// BRS 2.3.4 "Notifications to be implemented" table — the backend
// currently returns a single free-text message (see getErrorMessage in
// apiService.js), not a stable error code, so this maps that text back
// onto the exact wording the BRS specifies wherever we can recognise the
// failure. Anything that doesn't match one of these falls back to the
// backend's own message, and if there's no message at all, to the BRS
// "System Error" copy. If/when the backend starts returning a proper
// error code, replace this keyword matching with a direct code lookup —
// it will be far more reliable than string sniffing.
const BRS_ERROR_PATTERNS: { match: RegExp; message: string }[] = [
  {
    match: /invalid.*account|account.*(not found|invalid|verify)/i,
    message:
      "We couldn't verify your FTTHSA Account Number. Please check your details and try again or contact FTTHSA Support.",
  },
  {
    match: /duplicate/i,
    message:
      "This customer has already been referred. Only the first valid referral qualifies for the R100 reward.",
  },
  {
    match: /already.*(active )?customer|existing customer/i,
    message:
      "The person you referred is already an active FTTHSA customer and doesn't qualify for the referral programme.",
  },
  {
    match: /self.?referral/i,
    message:
      "Self-referrals are not permitted. Please refer a new FTTHSA customer to qualify for the R100 reward.",
  },
  {
    match: /consent/i,
    message: "Please confirm that your friend has agreed to be contacted before submitting your referral.",
  },
];

const SYSTEM_ERROR_MESSAGE =
  "Sorry, we couldn't submit your referral. Please try again later or contact FTTHSA Support if the issue continues.";

const mapReferralErrorMessage = (rawMessage?: string | null): string => {
  if (!rawMessage) return SYSTEM_ERROR_MESSAGE;
  const hit = BRS_ERROR_PATTERNS.find(({ match }) => match.test(rawMessage));
  return hit ? hit.message : rawMessage;
};

const Referral: React.FC = () => {
  const navigate = useNavigate();

  // FIX: campaign/building/QR identifiers from the sticker's QR code
  // (BR21 — QR scans/referrals must be traceable to campaign, building,
  // and QR ID). These arrive as query params on the redirect URL the QR
  // code points at, e.g. /referral?campaign=door-sticker-q3&building=B12&qr=QR00042.
  // If the actual param names Marketing/Max use differ, update the three
  // searchParams.get(...) calls below to match.
  const [searchParams] = useSearchParams();
  const campaignId = searchParams.get("campaign");
  const buildingId = searchParams.get("building");
  const qrCodeId = searchParams.get("qr");

  const [formData, setFormData] = useState({
    referrerName: "",
    referrerFtthNumber: "",
    referrerPhone: "",
    referredName: "",
    referredPhone: "",
    referredEmail: "",
    consent: false,
  });

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState<string | null>(null);

  // Auth state comes from the shared useAuth hook, so it updates the moment
  // login() succeeds anywhere (this popup, the header, another tab) — no
  // page refresh needed.
  const [authChecked, setAuthChecked] = useState(false);
  const { isLoggedIn: isAuthed } = useAuth();

  // Controls the actual popup. Auto-opens once we know the user isn't
  // authed, and can be dismissed/reopened independently of that check.
  const [showLoginModal, setShowLoginModal] = useState(false);

  // Which screen ReferralLoginModal is showing (login / forgot / confirm).
  // Drives the title in the modal header below.
  const [loginMode, setLoginMode] = useState<LoginMode>("login");

  // Pulled out of the mount-time effect so it can also be re-run right
  // after a successful login from ReferralLoginModal, without a page
  // reload or navigation away from /referral.
  const loadReferrerDetails = async (custId: string) => {
    try {
      setProfileLoading(true);
      setProfileError(null);

      const [userProfile, userSubscriptions] = await Promise.all([
        fetchUserProfile(custId),
        fetchUserSubscriptions(custId),
      ]);

      const fullName = [userProfile?.firstName, userProfile?.lastName]
        .filter(Boolean)
        .join(" ");

      const accountReference =
        Array.isArray(userSubscriptions) && userSubscriptions.length > 0
          ? userSubscriptions[0].accountReference
          : "";

      setFormData((prev) => ({
        ...prev,
        referrerName: fullName,
        referrerFtthNumber: accountReference || "",
        referrerPhone: userProfile?.mobileNumber || "",
      }));
    } catch (error) {
      console.error("Error loading referrer profile:", error);
      setProfileError("Couldn't load your details. Please refresh the page.");
    } finally {
      setProfileLoading(false);
    }
  };

  // Just marks that the first auth check has happened; auth itself now
  // comes from useAuth.
  useEffect(() => {
    setAuthChecked(true);
  }, []);

  // Whenever the user becomes logged in (from the popup, the header,
  // anywhere), close the popup and load their details — no refresh needed.
  useEffect(() => {
    if (!authChecked) return;

    if (!isAuthed) {
      setProfileLoading(false);
      return;
    }

    setShowLoginModal(false);

    let custId: string | null = null;
    try {
      custId = localStorage.getItem("portalEndCustomerId");
    } catch (err) {
      console.error(
        "Referral: localStorage access blocked (private mode / ITP):",
        err
      );
    }
    if (custId) loadReferrerDetails(custId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authChecked, isAuthed]);

  // Auto-open the login popup as soon as we've confirmed the visitor
  // isn't authed — this is what makes it appear the moment /referral
  // loads, like the screenshot.
  useEffect(() => {
    if (authChecked && !isAuthed) {
      setShowLoginModal(true);
    }
  }, [authChecked, isAuthed]);

  // Called by ReferralLoginModal after a successful login. Re-reads the
  // customer id that login() just wrote to localStorage, unlocks the
  // form, closes the popup, and loads the referrer's details — all
  // without leaving /referral. This is what makes the QR-code flow work
  // end to end: scan -> land on /referral -> log in in the popup -> form
  // unlocks.
  // The effect above reacts to useAuth flipping to logged-in, loads the
  // referrer details and closes the popup, so this only needs to close it.
  const handleReferralLoginSuccess = () => {
    setShowLoginModal(false);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  // Closing the success modal now also sends the user back home, so
  // "Done" reads as "I'm finished with this flow" rather than just
  // dismissing the popup and leaving them stranded on /referral.
  const handleCloseSuccessModal = () => {
    setSubmitted(false);
    setReferralCode(null);
    navigate("/");
  };

  const resetForm = () => {
    setFormData((prev) => ({
      ...prev,
      referredName: "",
      referredPhone: "",
      referredEmail: "",
      consent: false,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);

    if (
      !formData.referrerName ||
      !formData.referrerFtthNumber ||
      !formData.referrerPhone
    ) {
      alert(
        "We couldn't load your account details. Please refresh the page and try again."
      );
      return;
    }

    if (!formData.referredName || !formData.referredPhone) {
      alert("Please enter your friend's name and mobile number.");
      return;
    }

    if (!SA_MOBILE_PATTERN.test(formData.referredPhone.trim())) {
      alert("Please enter a valid 10-digit mobile number starting with 0 (e.g. 0821234567).");
      return;
    }

    // FIX (BR6 — self-referral prevention): the previous version relied
    // entirely on the backend to catch a referrer naming themselves as
    // the friend, and even then only surfaced whatever raw message came
    // back. Checking the phone number client-side gives an instant,
    // BRS-worded response instead of a round trip.
    if (formData.referredPhone.trim() === formData.referrerPhone.trim()) {
      alert(
        "Self-referrals are not permitted. Please refer a new FTTHSA customer to qualify for the R100 reward."
      );
      return;
    }

    if (!formData.consent) {
      alert("Please confirm that your friend has agreed to be contacted before submitting your referral.");
      return;
    }

    try {
      setLoading(true);

      // FIX: previously only referredName/referredPhone/consent were
      // sent — the friend's optional email, and the campaign/building/QR
      // identifiers from the sticker scan, were captured in state but
      // never left the browser. See the comment on createReferral() in
      // apiService.js for the caveat on whether the backend currently
      // stores these.
      const referralResult = await createReferral(
        formData.referredName,
        formData.referredPhone,
        formData.consent,
        {
          referredEmail: formData.referredEmail || undefined,
          campaignId: campaignId || undefined,
          buildingId: buildingId || undefined,
          qrCodeId: qrCodeId || undefined,
        }
      );

      if (!referralResult.success) {
        throw new Error(
          mapReferralErrorMessage(referralResult.message) ||
            "Failed to submit referral."
        );
      }

      setReferralCode(referralResult.data?.referralCode || null);
      setSubmitted(true);
      resetForm();
    } catch (error) {
      console.error(error);
      setErrorMsg(
        error instanceof Error
          ? mapReferralErrorMessage(error.message)
          : SYSTEM_ERROR_MESSAGE
      );
    } finally {
      setLoading(false);
    }
  };

  // The page shell (hero + benefits) always renders immediately, exactly
  // like Help.tsx always renders its layout. The form section reacts to
  // auth state: spinner while checking, the form once authed, or a CTA
  // card once we know they're logged out — with the actual login UI
  // living in the <Modal> below, popped up over the page.

  return (
    <div className="referral-page">
      {/* HERO */}
      <section
        className="referral-hero"
        style={{ backgroundImage: "url(/NEW.jpeg)" }}
      >
        <Container />
      </section>

      {/* BENEFITS */}
      <section className="referral-benefits">
        <Container>
          <Row className="g-4">
            <Col md={4}>
              <Card className="benefit-card h-100">
                <Card.Body>
                  <div className="benefit-icon">
                    <i className="bi bi-wifi"></i>
                  </div>
                  <h4>REFER</h4>
                  <p>Share FTTHSA with a friend who needs fast and reliable fibre.</p>
                </Card.Body>
              </Card>
            </Col>

            <Col md={4}>
              <Card className="benefit-card h-100">
                <Card.Body>
                  <div className="benefit-icon">
                    <i className="bi bi-lightning-charge"></i>
                  </div>
                  <h4>THEY CONNECT</h4>
                  <p>
                    Your friend signs up and gets connected to{" "}
                    <b style={{ color: "#e65c1c" }}>FTTHSA</b> fibre.
                  </p>
                </Card.Body>
              </Card>
            </Col>

            <Col md={4}>
              <Card className="benefit-card h-100">
                <Card.Body>
                  <div className="benefit-icon">
                    <i className="bi bi-gift"></i>
                  </div>
                  <h4>
                    YOU GET <b style={{ color: "#e65c1c" }}>R100</b>
                  </h4>
                  <p>
                    Once your friend is connected, you'll receive{" "}
                    <b style={{ color: "#e65c1c" }}>R100</b>, paid out the way
                    you set in your profile.
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* FORM SECTION */}
      <section id="referral-form" className="referral-form-section">
        <Container>
          <Row className="justify-content-center">
            <Col lg={8}>
              {!authChecked ? (
                // Auth status not yet known — show a spinner instead of
                // rendering nothing at all.
                <Card className="rf-card">
                  <Card.Body className="text-center py-5">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 mb-0">Checking your account...</p>
                  </Card.Body>
                </Card>
              ) : isAuthed ? (
                <Card className="rf-card">
                  <Card.Body>
                    <h2 className="form-title">Referral Form</h2>

                    {errorMsg && (
                      <div className="alert alert-danger">{errorMsg}</div>
                    )}

                    <form onSubmit={handleSubmit}>
                      {/* YOUR DETAILS */}
                      <div className="rf-section">
                        <div className="rf-section-header">
                          <span className="rf-label-plain">YOUR DETAILS</span>
                        </div>

                        {profileError && (
                          <div className="alert alert-danger">{profileError}</div>
                        )}

                        <div className="rf-grid rf-grid-2">
                          <div className="rf-field">
                            <label className="rf-field-label">FULL NAME</label>
                            <div className="rf-input-wrap">
                              <i className="bi bi-person rf-input-icon"></i>
                              <input
                                className="rf-input rf-input-readonly"
                                type="text"
                                name="referrerName"
                                value={
                                  profileLoading
                                    ? "Loading..."
                                    : formData.referrerName
                                }
                                readOnly
                              />
                            </div>
                          </div>

                          <div className="rf-field">
                            <label className="rf-field-label">ACCOUNT NUMBER</label>
                            <div className="rf-input-wrap">
                              <i className="bi bi-hash rf-input-icon"></i>
                              <input
                                className="rf-input rf-input-readonly"
                                type="text"
                                name="referrerFtthNumber"
                                value={
                                  profileLoading
                                    ? "Loading..."
                                    : formData.referrerFtthNumber
                                }
                                readOnly
                              />
                            </div>
                          </div>

                          <div className="rf-field rf-field-full">
                            <label className="rf-field-label">PHONE NUMBER</label>
                            <div className="rf-input-wrap">
                              <i className="bi bi-telephone rf-input-icon"></i>
                              <input
                                className="rf-input rf-input-readonly"
                                type="tel"
                                name="referrerPhone"
                                value={
                                  profileLoading
                                    ? "Loading..."
                                    : formData.referrerPhone
                                }
                                readOnly
                              />
                            </div>
                          </div>
                        </div>

                        <div className="rf-credit-note" style={{ marginTop: "1rem" }}>
                          <i className="bi bi-info-circle"></i>
                          <span>
                            Your R100 reward is paid out using the method saved
                            on your profile.{" "}
                            <Link to="/referral-preferences">
                              Update payout method
                            </Link>
                          </span>
                        </div>
                      </div>

                      {/* STEP 1: FRIEND'S DETAILS */}
                      <div className="rf-section">
                        <div className="rf-section-header">
                          <span className="rf-step-num">1</span>
                          <span className="rf-step-title">FRIEND'S DETAILS</span>
                          <span className="rf-section-line" />
                          <i className="bi bi-people rf-section-end-icon"></i>
                        </div>

                        <div className="rf-grid rf-grid-2">
                          <div className="rf-field">
                            <label className="rf-field-label">FRIEND'S FULL NAME</label>
                            <div className="rf-input-wrap">
                              <i className="bi bi-person rf-input-icon"></i>
                              <input
                                className="rf-input"
                                type="text"
                                name="referredName"
                                placeholder="Enter full name"
                                value={formData.referredName}
                                onChange={handleChange}
                                required
                              />
                            </div>
                          </div>

                          <div className="rf-field">
                            <label className="rf-field-label">FRIEND'S MOBILE NUMBER</label>
                            <div className="rf-input-wrap">
                              <i className="bi bi-phone rf-input-icon"></i>
                              <input
                                className="rf-input"
                                type="tel"
                                name="referredPhone"
                                placeholder="083 123 4567"
                                value={formData.referredPhone}
                                onChange={handleChange}
                                pattern="0[0-9]{9}"
                                maxLength={10}
                                title="Enter a 10-digit mobile number starting with 0"
                                required
                              />
                            </div>
                          </div>

                          <div className="rf-field rf-field-full">
                            <label className="rf-field-label">
                              EMAIL ADDRESS (OPTIONAL)
                            </label>
                            <div className="rf-input-wrap">
                              <i className="bi bi-envelope rf-input-icon"></i>
                              <input
                                className="rf-input"
                                type="email"
                                name="referredEmail"
                                placeholder="friend@example.com"
                                value={formData.referredEmail}
                                onChange={handleChange}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="rf-credit-note" style={{ marginTop: "1rem" }}>
                          <i className="bi bi-info-circle"></i>
                          <span>
                            That's all we need — your friend's installation
                            address and plan get sorted when they sign up. The
                            referral links automatically by their mobile number.
                          </span>
                        </div>
                      </div>

                      {/* STEP 2: CONSENT */}
                      <div className="rf-section">
                        <div className="rf-section-header">
                          <span className="rf-step-num">2</span>
                          <span className="rf-step-title">CONSENT</span>
                          <span className="rf-section-line" />
                          <i className="bi bi-shield-check rf-section-end-icon"></i>
                        </div>

                        <label className="rf-consent">
                          <input
                            type="checkbox"
                            name="consent"
                            checked={formData.consent}
                            onChange={handleChange}
                            required
                          />
                          <span>
                            I confirm that my friend has agreed to be contacted
                            by FTTHSA regarding fibre services.
                          </span>
                        </label>
                      </div>

                      {/* FOOTER */}
                      <div className="rf-footer">
                        <div className="rf-reward-note">
                          <span className="rf-reward-icon">
                            <i className="bi bi-gift"></i>
                          </span>
                          <span>
                            You'll receive your R100 reward once your friend's
                            fibre installation is completed and activated.{" "}
                            <Link to="/referral-history">
                              referral history
                            </Link>
                            .
                          </span>
                        </div>

                        <button
                          type="submit"
                          className="rf-submit-btn"
                          disabled={loading || profileLoading}
                        >
                          {loading ? "Submitting..." : "SUBMIT REFERRAL"}
                          <i className="bi bi-arrow-right rf-submit-icon"></i>
                        </button>
                      </div>
                    </form>
                  </Card.Body>
                </Card>
              ) : (
                // Not authed — the actual login UI lives in the popup
                // Modal below. This card is just the fallback shown
                // behind it (e.g. if someone dismisses the popup).
                <Card className="rf-card">
                  <Card.Body className="text-center py-5">
                    <p className="mb-3">Log in to unlock the referral form.</p>
                    <Button
                      className="rf-submit-btn"
                      onClick={() => setShowLoginModal(true)}
                    >
                      LOG IN
                    </Button>
                  </Card.Body>
                </Card>
              )}
            </Col>
          </Row>
        </Container>
      </section>

      {/* LOGIN MODAL — the only login modal. The orange header lives here;
          ReferralLoginModal just supplies the form content inside the body. */}
      <Modal
        show={showLoginModal}
        onHide={() => setShowLoginModal(false)}
        centered
      >
        <Modal.Header
          closeButton
          closeVariant="white"
          style={{ backgroundColor: "#e2520f", borderBottom: "none" }}
        >
          <Modal.Title style={{ color: "#ffffff", fontWeight: 600 }}>
            {LOGIN_TITLES[loginMode]}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ReferralLoginModal
            onLoginSuccess={handleReferralLoginSuccess}
            onModeChange={setLoginMode}
          />
        </Modal.Body>
      </Modal>

      {/* SUCCESS MODAL */}
      <Modal
        show={submitted}
        onHide={handleCloseSuccessModal}
        centered
        backdrop="static"
      >
        <Modal.Body className="rf-success-modal-body">
          <div className="rf-success-icon">
            <i className="bi bi-check-circle-fill"></i>
          </div>
          <h4 className="rf-success-title">Referral Submitted!</h4>
          <p className="rf-success-text">
            Thank you! Your referral has been submitted successfully. We'll
            be in touch once it's confirmed.
          </p>
          {referralCode && (
            <div className="rf-success-code">
              <span className="rf-success-code-label">REFERENCE NUMBER</span>
              <span className="rf-success-code-value">{referralCode}</span>
            </div>
          )}
          {/* FIX: previously the only option was "Done" -> home, leaving no
              visible link between "I just submitted this" and "here's how
              I track it". Status itself (Submitted -> ... -> Paid) lives on
              /referral-history via fetchReferrals(); this just makes sure
              the referrer can get there in one tap right after submitting. */}
          <p className="rf-success-text" style={{ marginTop: "0.5rem" }}>
            Status: <strong>Submitted</strong> — you can track its progress
            any time from your referral history.
          </p>
          <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center", flexWrap: "wrap" }}>
            {/* <Button
              variant="outline-secondary"
              className="rf-success-close-btn"
              onClick={handleViewHistory}
            >
              View Referral History
            </Button> */}
            <Button
              className="rf-success-close-btn"
              onClick={handleCloseSuccessModal}
            >
              Done
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default Referral;